import { useState } from 'react'
import type { CSSProperties } from 'react'

/**
 * Branded circular loader.
 *
 * The Insuronix mark sits still in the centre while a brand-blue arc sweeps
 * around it. The mark deliberately does NOT rotate - a spinning letterform
 * reads as a rendering fault rather than as progress.
 *
 * Use this for route transitions and whole-page waits. For content that has a
 * known shape (lists, cards, tables) prefer the `.ix-skeleton` blocks already
 * in use: they preview the layout instead of hiding it, which measures better
 * on perceived speed.
 */

export type LoaderSize = 'sm' | 'md' | 'lg'

const DIM: Record<LoaderSize, { box: number; ring: number; mark: number; stroke: number }> = {
  sm: { box: 28, ring: 28, mark: 13, stroke: 2.5 },
  md: { box: 48, ring: 48, mark: 22, stroke: 3 },
  lg: { box: 76, ring: 76, mark: 36, stroke: 4 },
}

interface LoaderProps {
  size?: LoaderSize
  /** Announced to screen readers; also rendered under the ring by FullPageLoader. */
  label?: string
  className?: string
  style?: CSSProperties
}

export function Loader({ size = 'md', label = 'Loading', className, style }: LoaderProps) {
  const d = DIM[size]
  const r = (d.ring - d.stroke) / 2
  const c = 2 * Math.PI * r
  // `Logo` has always had this and the loader did not, so where the mark
  // could not be fetched the middle of the ring was a broken-image glyph -
  // which reads as a rendering fault, exactly what the still mark exists to
  // avoid. The sweeping arc alone is still a loader.
  const [markFailed, setMarkFailed] = useState(false)

  return (
    <span
      className={className ? `ix-loader ${className}` : 'ix-loader'}
      style={{ width: d.box, height: d.box, ...style }}
      role="status"
      aria-live="polite"
      aria-label={label}
    >
      <svg className="ix-loader__ring" width={d.ring} height={d.ring} viewBox={`0 0 ${d.ring} ${d.ring}`} aria-hidden="true">
        <circle
          cx={d.ring / 2} cy={d.ring / 2} r={r}
          fill="none" stroke="var(--ix-blue-100)" strokeWidth={d.stroke}
        />
        <circle
          className="ix-loader__arc"
          cx={d.ring / 2} cy={d.ring / 2} r={r}
          fill="none" stroke="var(--ix-brand, #1058F8)" strokeWidth={d.stroke}
          strokeLinecap="round"
          strokeDasharray={`${c * 0.28} ${c * 0.72}`}
        />
      </svg>
      {markFailed ? null : (
        <img className="ix-loader__mark" src="/logo-mark.png" alt="" aria-hidden="true"
             onError={() => setMarkFailed(true)}
             style={{ width: d.mark, height: d.mark }} />
      )}
    </span>
  )
}

/** Centred loader for route transitions and whole-page waits. */
export function FullPageLoader({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="ix-loader-page">
      <Loader size="lg" label={label} />
      <p className="ix-loader-page__label">{label}</p>
    </div>
  )
}

/** Inline loader for buttons and toolbars. */
export function InlineLoader({ label = 'Working' }: { label?: string }) {
  return <Loader size="sm" label={label} className="ix-loader--inline" />
}

export default Loader
